import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaLinkedin } from 'react-icons/fa';
import { IoIosArrowUp } from 'react-icons/io';
import { useEffect } from 'react';

const BlogContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  padding: 2rem;
  background-color: #FCFCFC;
`;

const BlogGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const BlogCard = styled(motion(Link))`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  height: 280px;
  text-decoration: none;
  color: inherit;
`;

const BlogImage = styled(motion.div)`
  width: 400px;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
`;

const BlogContent = styled.div`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const BlogTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #333;
`;

const BlogExcerpt = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const Tag = styled(motion.span)`
  background-color: #f0f0f0;
  color: #666;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-family: 'Space Grotesk', sans-serif;
  transition: all 0.3s ease;

  &:hover {
    background-color: #007AFF;
    color: white;
  }
`;

const IntroSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 2rem;
`;

const IntroText = styled(motion.p)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.4rem;
  line-height: 1.6;
  color: #333;
  max-width: 900px;
  margin: 0 auto;
`;

const Footer = styled.footer`
  background-color: #f8f9fa;
  padding: 2rem 0;
  margin-top: 4rem;
  border-top: 1px solid #eaeaea;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif;
  transition: color 0.3s ease;

  &:hover {
    color: #007AFF;
  }
`;

const ScrollToTop = styled(motion.button)`
  background-color: #FFB100;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 177, 0, 0.2);

  &:hover {
    background-color: #FFC233;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 177, 0, 0.3);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ComingSoonContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  background: white;
  border-radius: 15px;
  padding: 3rem;
  margin: 2rem auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 800px;
`;

const ComingSoonTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 1.5rem;
`;

const ComingSoonText = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
  max-width: 600px;
`;

const Blog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <BlogContainer>
      <IntroSection>
        <IntroText
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          As a newcomer to research, I've started this blog to log my paper reading process. I find that rephrasing ideas is a great way to verify my understanding. In the vast graph of the research world, I hope these notes act as shared features — shaping my own representation while maybe reaching and resonating with neighboring nodes who share similar interests.
        </IntroText>
      </IntroSection>
      
      <BlogGrid>
        <ComingSoonContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ComingSoonTitle>Blog Posts Coming Soon</ComingSoonTitle>
          <ComingSoonText>
            I'm currently preparing thoughtful content on graph neural networks, machine learning research, and my journey in computer science. The first blog posts will be published shortly. Stay tuned!
          </ComingSoonText>
        </ComingSoonContainer>
      </BlogGrid>
      
      <Footer>
        <FooterContent>
          <ContactInfo>
            <ContactLink 
              href="mailto:ychen10001@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaEnvelope />
              ychen10001@gmail.com
            </ContactLink>
            <ContactLink 
              href="https://www.linkedin.com/in/yaqi-c-781b8729a/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaLinkedin />
              LinkedIn
            </ContactLink>
          </ContactInfo>
          <ScrollToTop
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <IoIosArrowUp size={24} />
          </ScrollToTop>
        </FooterContent>
      </Footer>
    </BlogContainer>
  );
};

export default Blog;
